import React, { ReactNode } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import { Checkbox } from "@components/checkbox";
import {
    rowSelectionCheckboxClass,
    selectBox,
} from "@components/table/table.styles";
import { DataValue, ITableColumn } from "../../table.types";

const SELECT_BOX = "select-box";

const formatCellView = (cellView: ReactNode) => {
    if (typeof cellView === "number") return cellView;
    if (typeof cellView === "boolean") return String(cellView);
    if (isEmpty(cellView)) return "-";

    return cellView;
};

const getCellView = <T,>(
    column: ITableColumn<T>,
    rowData: T,
    index: number
): ReactNode => {
    if (column.dataKey) {
        const columnData = (rowData as Record<string, DataValue>)[
            column.dataKey
        ];

        // when `dataKey` and `textRenderer` both are available
        if (column.textRenderer) {
            return formatCellView(column.textRenderer(columnData, index));
        }
        // when only `dataKey` is available
        return formatCellView(columnData);
    } else if (column.renderer) {
        // when only `renderer` is available
        return formatCellView(column.renderer(rowData, index));
    }

    return formatCellView(null);
};

export const getColumnsDefinitions = <T,>(
    columns: ITableColumn<T>[],
    enableMultiRowSelection?: boolean
): ColumnDef<T, ITableColumn<T>>[] => {
    const columnHelper = createColumnHelper<T>();
    const renderedColumns = columns.map(
        (column) =>
            columnHelper.accessor(
                (rowData, index) => getCellView(column, rowData, index),
                {
                    id: column.id,
                    cell: (info) => info.getValue(),
                    header: () => column.title,
                    meta: {
                        ...column,
                        ...(!column?.align && { align: "left" }),
                    },
                }
            ) as ColumnDef<T, ITableColumn<T>>
    );

    if (enableMultiRowSelection) {
        return [
            columnHelper.display({
                id: SELECT_BOX,
                header: ({ table }) => (
                    <Checkbox
                        className={rowSelectionCheckboxClass}
                        checked={table.getIsAllPageRowsSelected()}
                        indeterminate={
                            !table.getIsAllPageRowsSelected() &&
                            table.getIsSomePageRowsSelected()
                        }
                        onChange={(e) => {
                            table.getToggleAllRowsSelectedHandler()(e);
                        }}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        className={rowSelectionCheckboxClass}
                        checked={row.getIsSelected()}
                        disabled={!row.getCanSelect()}
                        onChange={(e) => {
                            row.getToggleSelectedHandler()(e);
                        }}
                    />
                ),
                meta: {
                    align: "left",
                    isActionColumn: true,
                    className: selectBox,
                    sortable: false,
                },
            }) as ColumnDef<T, ITableColumn<T>>,
            ...renderedColumns,
        ];
    }
    return renderedColumns;
};

export const getColumnsVisibility = (
    hiddenColumns: string[]
): Record<string, boolean> =>
    hiddenColumns.reduce(
        (columnsState, columnId) => {
            columnsState[columnId] = false;
            return columnsState;
        },
        {} as Record<string, boolean>
    );
